import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { useHomeState } from '../context/home-context';
import { PageSection, HomeSectionTitle } from '../home.styles';

// Material-UI Icons
import NewspaperIcon from '@mui/icons-material/Newspaper';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArticleIcon from '@mui/icons-material/Article';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Styled Components
const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e0e0e0;
`;

const Tab = styled.button<{ isActive: boolean }>`
  padding: 16px 32px;
  background: ${props => props.isActive ? '#0a5079' : 'transparent'};
  color: ${props => props.isActive ? '#fff' : '#0a5079'};
  border: none;
  border-bottom: ${props => props.isActive ? '3px solid #2786bd' : '3px solid transparent'};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.isActive ? '#0a5079' : '#eef8ff'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    font-size: 20px;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #2786bd;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  display: flex;
  align-items: center;

  svg {
    font-size: 20px;
  }
`;

const ListContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 24px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #2786bd;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #0a5079;
  }
`;

const ListItem = styled.div`
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: #eef8ff;
    border-color: #2786bd;
    transform: translateX(4px);
  }
`;

const ListItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const ItemIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #2786bd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    font-size: 24px;
  }
`;

const ItemText = styled.div`
  flex: 1;
`;

const ItemTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0a5079;
  margin-bottom: 4px;
`;

const ItemSubtitle = styled.div`
  font-size: 14px;
  color: #666;
`;

const Badge = styled.span`
  background: #2786bd;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
`;

const ArrowIconWrapper = styled.div`
  color: #2786bd;
  margin-left: 8px;
  display: flex;
  align-items: center;

  svg {
    font-size: 20px;
  }
`;

const SubcategoryContainer = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-left: 56px;
`;

const SubcategoryItem = styled.div`
  padding: 12px 16px;
  border-left: 3px solid #2786bd;
  margin: 8px 0;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background: #eef8ff;
    padding-left: 20px;
  }
`;

const CategoryItemWrapper = styled.div`
  margin-bottom: 8px;
`;

const ViewAllButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: #0a5079;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #023857;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ExpandIconWrapper = styled.div<{ isExpanded: boolean }>`
  color: #2786bd;
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};

  svg {
    font-size: 20px;
  }
`;

export default function SectionPublicationsTabs() {
  const { groupedDailyOfficial, categoriesWithSubcategories, loadingPublications } = useHomeState();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [searchYear, setSearchYear] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // Filtrar anos baseado na busca
  const filteredYears = useMemo(() => {
    if (!groupedDailyOfficial || groupedDailyOfficial.length === 0) {
      return [];
    }
    if (!searchYear.trim()) {
      return groupedDailyOfficial;
    }
    return groupedDailyOfficial.filter(item =>
      item.year?.toString().includes(searchYear.trim())
    );
  }, [groupedDailyOfficial, searchYear]);

  // Filtrar categorias baseado na busca
  const filteredCategories = useMemo(() => {
    if (!categoriesWithSubcategories || categoriesWithSubcategories.length === 0) {
      return [];
    }
    if (!searchCategory.trim()) {
      return categoriesWithSubcategories;
    }
    const searchLower = searchCategory.trim().toLowerCase();
    return categoriesWithSubcategories.filter(category => {
      const categoryMatch = category.name?.toLowerCase().includes(searchLower);
      const subcategoryMatch = category.subcategories?.some(sub =>
        sub.name?.toLowerCase().includes(searchLower)
      );
      return categoryMatch || subcategoryMatch;
    });
  }, [categoriesWithSubcategories, searchCategory]);

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    setSearchYear('');
    setSearchCategory('');
  };

  const handleCategoryClick = (categoryId: number) => {
    const hasSubcategories = categoriesWithSubcategories.find(c => c.id === categoryId)?.subcategories?.length || 0;

    if (hasSubcategories > 0) {
      setExpandedCategories(prev =>
        prev.includes(categoryId)
          ? prev.filter(id => id !== categoryId)
          : [...prev, categoryId]
      );
    } else {
      navigate(`/publicacoes/${categoryId}`);
    }
  };

  const handleSubcategoryNavigate = (categoryId: number, subcategoryId: number) => {
    navigate(`/publicacoes/${categoryId}/${subcategoryId}`);
  };

  // Se não há dados e não está carregando, não renderiza
  if (!loadingPublications &&
      (!groupedDailyOfficial || groupedDailyOfficial.length === 0) &&
      (!categoriesWithSubcategories || categoriesWithSubcategories.length === 0)) {
    return null;
  }

  return (
    <PageSection>
      <HomeSectionTitle>
        Diário Oficial e Publicações
        <hr />
      </HomeSectionTitle>
      <p style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
        Acesse os diários oficiais do município ou consulte as publicações organizadas por categoria.
      </p>

      <TabsContainer>
        <Tab
          isActive={tabValue === 0}
          onClick={() => handleTabChange(0)}
          disabled={!groupedDailyOfficial || groupedDailyOfficial.length === 0}
        >
          <NewspaperIcon />
          Diário Oficial
        </Tab>
        <Tab
          isActive={tabValue === 1}
          onClick={() => handleTabChange(1)}
          disabled={!categoriesWithSubcategories || categoriesWithSubcategories.length === 0}
        >
          <DescriptionIcon />
          Publicações
        </Tab>
      </TabsContainer>

      {/* Aba de Diário Oficial */}
      {tabValue === 0 && (
        <div>
          <SearchInputWrapper>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <SearchInput
              type="text"
              placeholder="Filtrar por ano (ex: 2024)"
              value={searchYear}
              onChange={(e) => setSearchYear(e.target.value)}
            />
          </SearchInputWrapper>

          <ListContainer>
            {filteredYears.length > 0 ? (
              filteredYears.map((item) => (
                <ListItem
                  key={item.year}
                  onClick={() => navigate(`/diario-oficial/${item.year}`)}
                >
                  <ListItemContent>
                    <ItemIcon>
                      <CalendarTodayIcon />
                    </ItemIcon>
                    <ItemText>
                      <ItemTitle>Diário Oficial - {item.year}</ItemTitle>
                      <ItemSubtitle>{item.quantity || 0} publicações</ItemSubtitle>
                    </ItemText>
                  </ListItemContent>
                  <ArrowIconWrapper>
                    <ArrowForwardIcon />
                  </ArrowIconWrapper>
                </ListItem>
              ))
            ) : (
              <EmptyState>
                {searchYear ? `Nenhum ano encontrado para "${searchYear}"` : 'Nenhum diário oficial disponível'}
              </EmptyState>
            )}
          </ListContainer>

          {filteredYears.length > 0 && (
            <ViewAllButton onClick={() => navigate('/diario-oficial')}>
              Ver todos os Diários Oficiais
            </ViewAllButton>
          )}
        </div>
      )}

      {/* Aba de Publicações */}
      {tabValue === 1 && (
        <div>
          <SearchInputWrapper>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <SearchInput
              type="text"
              placeholder="Filtrar por categoria ou subcategoria"
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.target.value)}
            />
          </SearchInputWrapper>

          <ListContainer>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => {
                const isExpanded = expandedCategories.includes(category.id);
                const hasSubcategories = category.subcategories && category.subcategories.length > 0;

                return (
                  <CategoryItemWrapper key={category.id}>
                    <ListItem onClick={() => handleCategoryClick(category.id)}>
                      <ListItemContent>
                        <ItemIcon>
                          <FolderOpenIcon />
                        </ItemIcon>
                        <ItemText>
                          <ItemTitle>{category.name}</ItemTitle>
                          <ItemSubtitle>
                            {hasSubcategories
                              ? `${category.subcategories.length} subcategorias`
                              : 'Sem subcategorias'}
                          </ItemSubtitle>
                        </ItemText>
                        <Badge>{category.publicationCount || 0}</Badge>
                      </ListItemContent>
                      {hasSubcategories ? (
                        <ExpandIconWrapper isExpanded={isExpanded}>
                          <ExpandMoreIcon />
                        </ExpandIconWrapper>
                      ) : (
                        <ArrowIconWrapper>
                          <ArrowForwardIcon />
                        </ArrowIconWrapper>
                      )}
                    </ListItem>

                    {hasSubcategories && (
                      <SubcategoryContainer isExpanded={isExpanded}>
                        {category.subcategories.map((subcategory) => (
                          <SubcategoryItem
                            key={subcategory.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubcategoryNavigate(category.id, subcategory.id);
                            }}
                          >
                            <ItemText>
                              <ItemTitle style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <ArticleIcon style={{ fontSize: '16px' }} />
                                {subcategory.name}
                              </ItemTitle>
                              <ItemSubtitle style={{ fontSize: '12px', paddingLeft: '24px' }}>
                                {subcategory.publicationCount || 0} publicações
                              </ItemSubtitle>
                            </ItemText>
                            <ArrowIconWrapper>
                              <ArrowForwardIcon />
                            </ArrowIconWrapper>
                          </SubcategoryItem>
                        ))}
                      </SubcategoryContainer>
                    )}
                  </CategoryItemWrapper>
                );
              })
            ) : (
              <EmptyState>
                {searchCategory ? `Nenhuma categoria encontrada para "${searchCategory}"` : 'Nenhuma categoria disponível'}
              </EmptyState>
            )}
          </ListContainer>

          {filteredCategories.length > 0 && (
            <ViewAllButton onClick={() => navigate('/publicacoes')}>
              Ver todas as Publicações
            </ViewAllButton>
          )}
        </div>
      )}
    </PageSection>
  );
}
